import styles from './DeliveryTypes.module.scss';
import { SupplierDeliveryType } from 'types';
import { RemoveDeliveryTypeCell } from '../RemoveDeliveryTypeCell/RemoveDeliveryTypeCell';
import { WillCallCell } from '../WillCallCell/WillCallCell';
import { Add } from '@mui/icons-material';
import { AddDeliveryTypeModal } from './AddDeliveryTypeModal/AddDeliveryTypeModal';
import { DataTable, DataTableProps } from 'components/data/DataTable/DataTable';
import { OpenModalButton } from 'components/button/OpenModalButton/OpenModalButton';

export interface DeliveryTypesProps {
    deliveryTypes: SupplierDeliveryType[];
    supplierId: string;
    /** Callback to alert parent component that delivery types has been updated. */
    updateDeliveryTypes: React.Dispatch<
        React.SetStateAction<SupplierDeliveryType[]>
    >;
}

export const DeliveryTypes = ({
    deliveryTypes,
    supplierId,
    updateDeliveryTypes,
}: DeliveryTypesProps) => {
    const removeDeliveryType = (deliveryTypeName: string): void => {
        const updatedDeliveryTypes = deliveryTypes.filter(
            (deliveryType: SupplierDeliveryType) => {
                return deliveryType.deliveryTypeName !== deliveryTypeName;
            }
        );

        updateDeliveryTypes(updatedDeliveryTypes);
    };

    const addDeliveryType = (newDeliveryType: SupplierDeliveryType): void => {
        const updatedDeliveryTypes = [...deliveryTypes, newDeliveryType];
        updateDeliveryTypes(updatedDeliveryTypes);
    };

    const updateDeliveryTypeWillCall = (
        updatedDeliveryType: SupplierDeliveryType
    ): void => {
        const updatedDeliveryTypes = deliveryTypes.map(
            (deliveryType: SupplierDeliveryType) => {
                // Find the delivery type we are updating and update the will call value.
                if (
                    deliveryType.deliveryTypeName ===
                    updatedDeliveryType.deliveryTypeName
                ) {
                    deliveryType.willCall = updatedDeliveryType.willCall;
                }
                return deliveryType;
            }
        );
        updateDeliveryTypes(updatedDeliveryTypes);
    };

    const columns: DataTableProps['columns'] = [
        {
            field: 'deliveryTypeName',
            headerName: 'Name',
            flex: 1,
            sortingOrder: ['asc', 'desc'],
        },
        {
            field: 'willCall',
            headerName: 'Will Call',
            flex: 1,
            renderCell: (params) => (
                <WillCallCell
                    supplierId={supplierId}
                    params={params}
                    onWillCallUpdate={updateDeliveryTypeWillCall}
                />
            ),
            sortingOrder: ['asc', 'desc'],
            maxWidth: 100,
        },
        {
            field: 'remove',
            headerName: 'Remove',
            renderCell: (params) => (
                <RemoveDeliveryTypeCell
                    params={params}
                    supplierId={supplierId}
                    onRemoveDeliveryType={removeDeliveryType}
                />
            ),
            sortable: false,
            maxWidth: 100,
            disableExport: true,
        },
    ];

    return (
        <div data-testid="DeliveryTypes" className={styles.container}>
            <DataTable
                id="organizationDeliveryTypes"
                columns={columns}
                data={deliveryTypes ?? []}
                dataFormatter={(deliveryTypes) => deliveryTypes}
                getRowId={(deliveryType) => deliveryType.deliveryTypeName}
                getRowCount={() => deliveryTypes.length}
                disableSelectionOnClick
                initialState={{
                    sorting: {
                        sortModel: [{ field: 'deliveryTypeName', sort: 'asc' }],
                    },
                }}
                disableColumnMenu
                csvOptions={{
                    fileName: 'delivery-types',
                }}
                toolbarComponents={
                    <OpenModalButton
                        icon={<Add />}
                        modal={AddDeliveryTypeModal}
                        className={styles.openModal}
                        modalProps={{
                            supplierId: supplierId,
                            onAddDeliveryType: addDeliveryType,
                            deliveryTypes: deliveryTypes,
                        }}
                    >
                        Add Delivery Type
                    </OpenModalButton>
                }
            />
        </div>
    );
};
